import { useState, useEffect, useRef } from 'react';
import { getSignature, saveSignature, deleteSignature } from '../../lib/db';
import { useAuth } from '../../lib/auth';
import { Trash2, Upload, Pen } from 'lucide-react';

export function SignatureManager() {
  const user = useAuth(s => s.user);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'view' | 'draw'>('view');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);

  useEffect(() => {
    if (user?.id) {
      getSignature(user.id).then(data => {
        setSignatureData(data);
        setLoading(false);
      });
    }
  }, [user?.id]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    isDrawing.current = true;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1f2937';
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    isDrawing.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveDrawing = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !user?.id) return;
    setSaving(true);
    try {
      const dataUrl = canvas.toDataURL('image/png');
      await saveSignature(user.id, dataUrl);
      setSignatureData(dataUrl);
      setMode('view');
    } catch (err) {
      console.error('Error saving signature:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    setSaving(true);
    try {
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
      await saveSignature(user.id, dataUrl);
      setSignatureData(dataUrl);
    } catch (err) {
      console.error('Error uploading signature:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      await deleteSignature(user.id);
      setSignatureData(null);
    } catch (err) {
      console.error('Error deleting signature:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-gray-500">Cargando...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-800">Firma Digital</h2>
      <p className="text-sm text-gray-500">Tu firma se incluirá automáticamente en los boletines de notas PDF.</p>

      {mode === 'view' ? (
        <div className="space-y-4">
          {signatureData ? (
            <div className="space-y-3">
              <div className="p-4 bg-gray-50 rounded-lg border inline-block">
                <img src={signatureData} alt="Firma" className="max-w-xs max-h-24 object-contain" />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setMode('draw')}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100"
                >
                  <Pen className="w-4 h-4" /> Dibujar nueva
                </button>
                <label className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 cursor-pointer">
                  <Upload className="w-4 h-4" /> Subir imagen
                  <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
                </label>
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100"
                >
                  <Trash2 className="w-4 h-4" /> Eliminar
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-center items-center w-64 h-24 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <span className="text-sm text-gray-400">Sin firma</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setMode('draw')}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100"
                >
                  <Pen className="w-4 h-4" /> Dibujar firma
                </button>
                <label className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 cursor-pointer">
                  <Upload className="w-4 h-4" /> Subir imagen
                  <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
                </label>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Dibuja tu firma en el recuadro de abajo:</p>
          <canvas
            ref={canvasRef}
            width={400}
            height={150}
            className="bg-white rounded-lg border-2 border-gray-300 cursor-crosshair"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
          />
          <div className="flex gap-2">
            <button
              onClick={saveDrawing}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar firma'}
            </button>
            <button
              onClick={clearCanvas}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Limpiar
            </button>
            <button
              onClick={() => setMode('view')}
              className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
